import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API } from '@configs/api.config';
import { VehicleCategory } from '@shared/models/vehicle-category.model';
import { Organization } from 'app/modules/organization/models/organization';
import { CollaboratorsInvitationModel } from 'app/modules/wizard/models/collaborators-invitation.model';
import { Observable } from 'rxjs';
import { DigitalTwinFilter, DigitalTwinVehicleDTO, SpringPage, UtilizationImpact, VehicleCategorySummary, VehicleSelectionIntent } from '../models/digital-twin.model';

@Injectable({
  providedIn: 'root'
})
export class JobPlaningService {

  constructor(private httpClient: HttpClient) { }

  getJobPlaning(orgId: string, jobRefId: string): Observable<any> {
    const url = "";
    return this.httpClient.get<any>(url);
  }

  getVehicleCategories(isStandard?: boolean): Observable<VehicleCategory[]> {
    let params = new HttpParams();
    if (isStandard !== undefined) {
      params = params.append('isStandard', isStandard ? isStandard.toString(): 'false');
    }
    const url = API.organization.getVehicleCategories;
    return this.httpClient.get<VehicleCategory[]>(url, {params});
  }

  getOrganizationsByOrgIdAndOrgType(orgId: string, orgType: string, searchKey: string): Observable<Organization[]> {
    const url = API.organization.getOrganizationsByOrgIdAndOrgType.format([orgId, orgType]);
    let params = new HttpParams();
    params = params.append('searchKey', String(searchKey));
    return this.httpClient.get<Organization[]>(url, {params});
  }

  getDigitalTwinVehicles(payload: {
    orgId: string;
    operationId: string;
    vehicleCategory: string;
    supplierIds: string[];
    searchText: string;
    type: string;
  }, page: number = 0, size: number = 20): Observable<any> {
    const url = API.organization.getDigitalTwinVehicles.format([]);
    let params = new HttpParams();
    params = params.set('page', String(page));
    params = params.set('size', String(size));
    return this.httpClient.post<any>(url, payload, { params });
  }

  inviteCollaborators(collaboratorsInvitationModel: CollaboratorsInvitationModel): Observable<any> {
    const url = API.jobFile.inviteCollaborators;
    return this.httpClient.post<any>(url, collaboratorsInvitationModel);
  }

  getInternalExternalVehicles(
    operationId: string,
    pageNum: number = 0,
    pageSize: number = 100
  ): Observable<any> {
    const url = API.organization.getInternalExternalVehicles.format([]);
    let params = new HttpParams()
      .set('operationId', operationId)
      .set('pageNum', String(pageNum))
      .set('pageSize', String(pageSize));
    return this.httpClient.get<any>(url, { params: params });
  }

  // ---- Paginated digital-twin selector (vehicle pagination plan) ----

  /** Categories with their vehicle counts for the filter — no fleet load. */
  getDigitalTwinCategorySummary(filter: DigitalTwinFilter): Observable<VehicleCategorySummary[]> {
    const url = API.organization.getDigitalTwinCategorySummary.format([]);
    return this.httpClient.post<VehicleCategorySummary[]>(url, filter);
  }

  /** One slim page of vehicles for a category (+ optional vehicle-number search). */
  getDigitalTwinVehiclesPage(
    filter: DigitalTwinFilter,
    pageNumber: number = 0,
    pageSize: number = 20
  ): Observable<SpringPage<DigitalTwinVehicleDTO>> {
    const url = API.organization.getDigitalTwinVehiclesPage.format([]);
    const params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));
    return this.httpClient.post<SpringPage<DigitalTwinVehicleDTO>>(url, filter, { params });
  }

  /** All vehicle ids matching the filter (category/search) — lightweight, for select-all. */
  getDigitalTwinVehicleIds(filter: DigitalTwinFilter): Observable<string[]> {
    const url = API.organization.getDigitalTwinVehicleIds.format([]);
    return this.httpClient.post<string[]>(url, filter);
  }

  /** Bulk-fetch full vehicle data for a set of ids (POST — ids in body). */
  getVehiclesByIds(ids: string[]): Observable<any[]> {
    const url = API.organization.getVehiclesByIds.format([]);
    return this.httpClient.post<any[]>(url, ids);
  }

  /**
   * Paginated selector init: returns the category summary for the UI and, server-side, resolves +
   * persists the real vehicle categories/vehicle data into the jobfile. The browser gets only
   * names + counts.
   */
  initVehicleSelection(orgId: string, jobRefId: string, operationId: string): Observable<VehicleCategorySummary[]> {
    const url = API.jobFile.initVehicleSelection.format([orgId, jobRefId]);
    const params = new HttpParams().set('operationId', operationId || '');
    return this.httpClient.post<VehicleCategorySummary[]>(url, {}, { params });
  }

  /**
   * Paginated vehicle rows FROM THE JOBFILE (the saved/edited data). ms-org-manager is only used to
   * seed the jobfile once; after that the jobfile is the source of truth, so re-opening the selector
   * never overwrites the user's selection/utilization edits.
   */
  getJobFileVehiclePage(
    orgId: string, jobRefId: string, categoryId: string, search: string,
    pageNumber: number = 0, pageSize: number = 20
  ): Observable<SpringPage<any>> {
    const url = API.jobFile.getJobFileVehiclePage.format([orgId, jobRefId]);
    const params = new HttpParams()
      .set('categoryId', categoryId || '')
      .set('search', search || '')
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize));
    return this.httpClient.get<SpringPage<any>>(url, { params });
  }

  /**
   * How many stored vehicles carry an individually-edited utilization. Paginated mode holds no
   * vehicle data in the browser, so only the backend can answer whether changing the global
   * utilization would overwrite per-vehicle edits.
   */
  getUtilizationImpact(orgId: string, jobRefId: string): Observable<UtilizationImpact> {
    const url = API.jobFile.getUtilizationImpact.format([orgId, jobRefId]);
    return this.httpClient.get<UtilizationImpact>(url);
  }

  /** Persist the compact vehicle-selection intent to the jobfile (materialized server-side). */
  saveVehicleSelectionIntent(orgId: string, jobRefId: string, intent: VehicleSelectionIntent): Observable<any> {
    const url = API.jobFile.saveVehicleSelectionIntent.format([orgId, jobRefId]);
    return this.httpClient.post<any>(url, intent);
  }
}
